<section
  class="step-shell"
  [class.step-shell--authoring]="isDetailAuthoring"
  aria-label="Workflow workspace"
>
  <p *ngIf="confirmation() && !state() && !loading() && !accessDenied()" class="save-confirmation" role="status">
    {{ confirmation() }}
  </p>
  <ui-standard-state
    *ngIf="readState() as kind"
    [kind]="kind"
    [message]="kind === 'loading' ? 'Loading workflow workspace…' : error()"
    [recoveryLabel]="
      previewMode
        ? undefined
        : accessDenied()
          ? 'Check access'
          : 'Retry workspace'
    "
    [recoveryDisabled]="isSaving() || visitControlInputPending()"
    (recovery)="load()"
  />
  <ez-workspace-frame
    *ngIf="state() as data"
    [contextPlacement]="data.detail.contextPlacement" [hasRequirementsRail]="true"
    [compactSideRail]="isDetailAuthoring" [contextFirstOnSmallScreens]="!isDetailAuthoring" [independentScrolling]="!previewMode"
  >
    <div frameSummary>
      <phms-workflow-summary-bar
        [compact]="true"
        [ledger]="ledger()"
        [state]="ledgerState()"
        [expanded]="historyExpanded()"
        (expandedChange)="historyExpanded.set($event)"
        [controlsId]="historyId"
      >
        <div summaryIdentity class="case-identity">
          <strong *ngIf="data.subject.type; else processIdentity"
            >{{ displayStatus(data.subject.type) }}
            {{ data.subject.id }}</strong
          >
          <ng-template #processIdentity
            ><strong
              >{{ data.processName || data.execution.workflowCode }} · Run
              {{ data.execution.workflowInstanceId }}</strong
            ></ng-template
          >
          <span
            >Workflow reference:
            {{ data.execution.publicReference || "Not assigned" }}</span
          >
          <span *ngIf="data.execution.definitionVersionCode">Version {{data.execution.definitionVersionCode}}</span>
        </div>
      </phms-workflow-summary-bar>
      <ui-button
        *ngIf="ledgerState() === 'error' || ledgerState() === 'unavailable'"
        [buttonText]="
          historyExpanded() ? 'Hide recorded visits' : 'View recorded visits'
        "
        size="small"
        severity="secondary"
        [ariaExpanded]="historyExpanded()"
        [ariaControls]="historyId"
        (click)="historyExpanded.set(!historyExpanded())"
      />
      <section
        *ngIf="historyExpanded()"
        [id]="historyId"
        aria-label="Workflow details and history"
        class="history-panel"
      >
        <phms-workflow-ledger
          [ledger]="ledger()"
          [state]="ledgerState()"
          [compact]="true"
        />
        <ul class="review-history" *ngIf="data.execution.reviewHistory?.length">
          <li *ngFor="let review of data.execution.reviewHistory">
            <strong
              >{{ displayStatus(review.stepCode) }} · Visit
              {{ review.occurrenceNumber }} ·
              {{ displayStatus(review.statusCode) }}</strong
            >
            <p *ngIf="reviewHistoryWorkSummary(review) as summary">Submitted work: {{ summary }}</p>
            <p>
              {{ reviewHistoryNotes(review) }}
            </p>
          </li>
        </ul>
        <nav *ngIf="!previewMode" aria-label="Step occurrences">
          <a
            *ngFor="let step of data.execution.occurrences"
            [routerLink]="[
              '/workflows',
              data.execution.workflowInstanceId,
              'steps',
              step.id,
            ]"
            [queryParams]="visitQueryParams()"
            [attr.aria-current]="
              step.id === data.selectedOccurrenceId ? 'step' : null
            "
            >{{ displayStatus(step.stepCode) }} · Visit
            {{ step.occurrenceNumber }} ·
            {{ displayStatus(step.statusCode) }}</a
          >
        </nav>
        <p *ngIf="!data.execution.occurrences.length" class="history-empty">
          No step visits have been recorded for this run.
        </p>
      </section>
    </div>
    <div frameToolbar class="workspace-bars">
      <ez-workspace-toolbar kind="navigation" label="Page navigation">
        <div barStart class="task-identity">
          <nav
            *ngIf="!previewMode && backToQueue"
            class="page-toolbar"
            aria-label="Workspace navigation"
          >
            <a
              [routerLink]="backToQueue"
              [attr.aria-label]="backLinkLabel()"
              [title]="backLinkLabel()"
              ><i class="pi pi-arrow-left" aria-hidden="true"></i>
              {{ backToReferenceSearch ? "Back to reference search" : backToProcessRuns ? "Back to process runs" : backToWorkflowOverview ? "Back to workflow overview" : "Dashboard" }}</a
            >
            <a *ngIf="sourceRecordLink() as source" [routerLink]="source.url"
              data-testid="workflow-source-record">{{ source.label }}</a>
          </nav>
          <h1>{{ data.stepLabel || data.processName || "Workflow" }}</h1>
          <span
            class="visit-label"
            *ngIf="data.context.workflow?.occurrenceNumber as visit"
            >Visit {{ visit }}</span
          >
          <span class="status">Step: {{ stepProgressLabel() }}</span>
          <span class="status" *ngIf="!previewMode && !isEditableVisit()"
            >Read-only</span
          >
        </div>
        <ui-button
          barEnd
          *ngIf="!previewMode"
          buttonText="Refresh"
          pIcon="pi-refresh"
          severity="secondary"
          size="small"
          (click)="load()"
          [disabled]="loading() || isSaving() || visitControlInputPending()"
          [tooltip]="visitControlInputPending() ? 'Save or cancel your changes before refreshing this workspace.' : undefined"
        />
      </ez-workspace-toolbar>
    </div>
    <div frameMain class="workspace-main">
      <p
        *ngIf="confirmation()"
        class="save-confirmation"
        role="status"
        aria-live="polite"
      >
        {{ confirmation() }}
      </p>
      <section *ngIf="recoveredDraft()" aria-label="Recovered input">
        <h2>Unsaved input from this workspace</h2>
        <p>
          This input has not been saved or applied to the current work. Copy
          anything you need before leaving.
        </p>
        <textarea
          aria-label="Preserved input"
          readonly
          [value]="recoveredDraft()"
        ></textarea>
      </section>
      <ui-tab-group [navigationTabs]="pageTabs()" [activeTabId]="selectedPageTabId()"
        navigationAriaLabel="Page content" [stretchTabs]="false" (tabRequested)="selectPageTab($event)">
      <ui-panel
        *ngIf="displayForm() as definition"
        [hidden]="!formOnActiveTab()" [attr.inert]="!formOnActiveTab() ? '' : null"
        [class.detail-form--selected]="isDetailAuthoring && formSelected"
        [panelTitle]="
          action()?.form?.captureForm?.title ||
          action()?.label ||
          'Selected action'
        "
        aria-label="Selected action"
      >
        <div class="panel-body">
          <p *ngIf="!previewMode && isSaveOnlyForm()" class="submission-hint">
            Saved answers are final for this visit. Review them before saving.
          </p>
          <ng-container
            *ngIf="isDetailAuthoring"
            [ngTemplateOutlet]="formHeader"
          />
          <p *ngIf="displayOnlyPreviewForm()" class="submission-hint" role="status" data-lookup-preview>
            Lookup choices are unavailable in this preview. The saved form is shown read-only; choices load in the staff task.
          </p>
          <phms-generated-command-form
            [definition]="formWording(definition).definition"
            [terminologyControls]="formWording(definition).controls"
            [values]="displayOnlyPreviewForm() ? emptyPreviewValues : values"
            (valuesChange)="updateValues($event)"
            [issues]="issues"
            [disabled]="
              !!displayOnlyPreviewForm() ||
              (previewMode && previewReadOnly) ||
              isWaitingForStart() ||
              isSaving() ||
              !action()?.enabled
            "
            [authoring]="isDetailAuthoring"
            [selectedField]="selectedField"
            [authoringIssues]="selectedFieldIssues"
            [reorderable]="isDetailAuthoring && isCaptureForm"
            (fieldConfigure)="configureField($event)"
            (fieldOrderChange)="reorderFields($event)"
          />
          <div *ngFor="let lookup of lookupStatusRows(); trackBy: trackLookupStatus" class="lookup-status" [attr.data-lookup-field]="lookup.id" tabindex="-1" role="status">
            <p><strong>{{ lookup.label }}:</strong> {{ lookup.message }}</p>
            <button *ngIf="lookup.canClear" uiButton type="button" (click)="clearLookupValue(lookup.id, lookup.signature)"
              [attr.aria-label]="'Clear ' + lookup.label + ' value'">Clear value</button>
          </div>
          <div
            *ngIf="reviewReasonPresentation() as reason"
            class="reason-dialog-action"
          >
            <ui-button
              [buttonText]="values['notes'] ? 'Edit reason' : 'Add reason'"
              severity="secondary"
              size="small"
              [disabled]="!canEditReviewReason()"
              (click)="editReviewReason()"
            />
            <p *ngIf="reason.unavailable" role="alert">
              {{ reason.unavailable }}
            </p>
          </div>
        </div>
      </ui-panel>
      <ng-template #formHeader>
        <button
          type="button"
          class="detail-panel-title"
          (click)="configureForm()"
          [attr.aria-pressed]="formSelected"
          [attr.aria-label]="
            'Configure form ' +
            (action()?.form?.captureForm?.title ||
              action()?.label ||
              'Selected action')
          "
        >
          <strong>{{
            action()?.form?.captureForm?.title ||
              action()?.label ||
              "Selected action"
          }}</strong
          ><i class="pi pi-pencil" aria-hidden="true"></i>
        </button>
      </ng-template>
      <p
        *ngIf="
          action() &&
          !displayForm() &&
          !selectedFormSaved() &&
          !selectedDecisionRecorded()
        "
        role="status"
      >
        A compatible interaction is not installed for this selected action.
      </p>
      <div
        *ngIf="isDetailAuthoring && allowedBlockRegions().includes('main')"
        class="detail-region-heading"
        role="group"
        aria-label="Main content controls"
      >
        <strong>Main content</strong>
        <button
          type="button"
          class="detail-insert-button"
          [disabled]="detailEditingDisabled"
          (click)="addPageContent('main')"
        >
          <i class="pi pi-plus" aria-hidden="true"></i> Add main content
        </button>
        <button
          *ngIf="state()?.detail?.contextPlacement === 'hidden'"
          type="button"
          class="detail-edit-button"
          aria-label="Reference rail placement"
          (click)="configureDetail({kind:'context'})"
        >
          <i class="pi pi-pencil" aria-hidden="true"></i> Reference rail: hidden
        </button>
      </div>
      <ez-workspace-block-host
        *ngIf="
          session &&
          (mainBlocks.length ||
            (isDetailAuthoring && allowedBlockRegions().includes('main')))
        "
        [registry]="registry"
        [session]="session"
        host="step-shell"
        [blocks]="mainBlocks"
        [activeTabId]="selectedPageTabId()"
        [authoring]="isDetailAuthoring"
        authoringRegion="main"
        [authoringGroup]="historyId"
        [allowedRegions]="allowedBlockRegions()"
        [selectedInstanceId]="selectedBlock"
        [authoringEmptyTemplate]="emptyPageContent"
        (blockConfigure)="configureDetail({ kind: 'block', id: $event })"
        (blockMove)="moveBlock($event)"
      />
      <p *ngIf="pageTabs().length && !isDetailAuthoring && !hasActiveTabContent()">No information on this tab.</p>
      </ui-tab-group>
      <ng-template #emptyPageContent let-region>
        <div
          class="detail-content-empty"
          role="group"
          [attr.aria-label]="
            region === 'main' ? 'Main content' : 'Reference rail'
          "
        >
          <p>
            {{
              region === "main"
                ? "Add instructions or information alongside the task form."
                : "Add reference information to help staff complete the task."
            }}
          </p>
        </div>
      </ng-template>
      <ui-panel
        *ngIf="!isEditableVisit()"
        [panelTitle]="recordedWorkTitle()"
        aria-label="Recorded work"
      >
        <div class="panel-body recorded-work">
          <p>
            {{ recordedWorkDescription() }}
          </p>
          <section
            *ngFor="let review of selectedReviews()"
            class="recorded-review"
            aria-label="Recorded review"
          >
            <h2>{{ displayStatus(review.statusCode) }}</h2>
            <p *ngIf="!data.selectedOccurrenceId">
              {{ displayStatus(review.stepCode) }} · Visit
              {{ review.occurrenceNumber }}
            </p>
            <p *ngIf="reviewHistoryWorkSummary(review) as summary">Submitted work: {{ summary }}</p>
            <p class="recorded-notes">
              {{ reviewHistoryNotes(review) || "No review notes recorded." }}
            </p>
          </section>
          <p *ngIf="!data.execution.occurrences.length">
            No step visits were recorded before this run ended. The summary and
            history remain available for reference.
          </p>
          <p
            *ngIf="
              data.execution.occurrences.length &&
              !selectedReviews().length &&
              !data.formSubmissions?.length
            "
          >
            No review result is available for this visit with your current
            access. Recorded activity is available in workflow history.
          </p>
          <nav
            *ngIf="!previewMode"
            class="next-destinations"
            aria-label="Next destination"
          >
            <a
              *ngIf="hasCurrentWork()"
              [routerLink]="['/workflows', data.execution.workflowInstanceId]"
              [queryParams]="{ returnTo: returnTo }"
              >Open current work</a
            >
            <a *ngIf="backToQueue" [routerLink]="backToQueue">{{
              backToReferenceSearch ? "Back to reference search" : backToProcessRuns ? "Back to process runs" : backToWorkflowOverview ? "Back to workflow overview" : "Return to dashboard"
            }}</a>
          </nav>
        </div>
      </ui-panel>
      <phms-workflow-form-submissions
        [submissions]="data.formSubmissions || []"
        [selectedOccurrenceId]="data.selectedOccurrenceId"
      />
      <phms-workflow-documents
        *ngIf="!hasDocumentsBlock"
        [workflowId]="data.execution.workflowInstanceId"
        [selectedOccurrenceId]="data.selectedOccurrenceId"
        [documents]="data.documentSubmissions || []"
        [hasRequirements]="!!data.hasDocumentRequirements"
        [disabled]="previewMode || loading() || isSaving() || reloadRequired()"
      />
      <ui-panel
        *ngIf="
          isEditableVisit() &&
          !action() &&
          !completeAction() &&
          !isWaitingForStart() &&
          !mainBlocks.length
        "
        panelTitle="Work is unavailable"
        aria-label="Task availability"
      >
        <div class="panel-body">
          <p>
            No task is available for you at this visit. Check the action
            guidance in the right rail or return to the dashboard.
          </p>
        </div>
      </ui-panel>
      <ng-container [ngTemplateOutlet]="operationalPanels" />
    </div>
    <div frameRequirementsRail class="context work-rail" role="region" aria-label="Actions and requirements">
      <ui-panel panelTitle="Actions &amp; requirements" aria-label="Actions and requirements" [totalCount]="completionChecklist()?.requirements?.length">

      <div
        *ngIf="
          data.nodeTypeCode !== 'automated_step' &&
          data.stepProgress as progress
        "
        class="step-progress"
        role="group"
        aria-label="Step progress"
      >
        <p
          *ngIf="
            progress.statusCode === 'pending' ||
            progress.statusCode === 'assigned'
          "
        >
          {{
            progress.statusCode === "assigned"
              ? "Assigned and waiting to begin."
              : "Waiting for work to begin."
          }}
          <span *ngIf="!progress.canStart">{{
            progress.reason ||
              "The assigned or an eligible staff member can start this work."
          }}</span>
        </p>
        <p *ngIf="progress.statusCode === 'bypassed'">
          Bypassed by a configured rule. {{ progress.reason }}
        </p>
        <ui-button
          *ngIf="
            progress.canStart &&
            (progress.statusCode === 'pending' ||
              progress.statusCode === 'assigned')
          "
          [buttonText]="startingWork() ? 'Starting work…' : 'Start work'"
          severity="primary"
          [disabled]="!canStartWork()"
          (click)="startWork()"
        />
        <p *ngIf="stepStartError()" role="alert">{{ stepStartError() }}</p>
      </div>
      <div
        *ngIf="data.businessStatus"
        class="rail-business-statuses"
        role="group"
        aria-label="Process and step statuses"
      >
        <phms-workflow-business-status
          *ngIf="data.businessStatus.workflow as processStatus"
          scope="workflow"
          [workflowId]="data.execution.workflowInstanceId"
          [occurrenceId]="data.selectedOccurrenceId"
          [status]="processStatus"
          [preview]="previewMode"
          [isCurrent]="data.isCurrent"
          [disabled]="
            loading() ||
            saving() ||
            startingWork() ||
            stepStatusSaving() ||
            hasUnsavedInput()
          "
          [disabledReason]="
            hasUnsavedInput()
              ? 'Save or cancel your current task before changing status.'
              : ''
          "
          (operationInProgressChange)="workflowStatusSaving.set($event)"
          (reloadRequested)="load()"
        />
        <phms-workflow-business-status
          *ngIf="data.businessStatus.step as stepStatus"
          scope="step"
          [workflowId]="data.execution.workflowInstanceId"
          [occurrenceId]="data.selectedOccurrenceId"
          [status]="stepStatus"
          [preview]="previewMode"
          [isCurrent]="data.isCurrent"
          [disabled]="
            loading() ||
            saving() ||
            startingWork() ||
            workflowStatusSaving() ||
            hasUnsavedInput()
          "
          [disabledReason]="
            hasUnsavedInput()
              ? 'Save or cancel your current task before changing status.'
              : ''
          "
          (operationInProgressChange)="stepStatusSaving.set($event)"
          (reloadRequested)="load()"
        />
      </div>
      <ez-workspace-toolbar
        *ngIf="
          data.nodeTypeCode !== 'automated_step' &&
          (isEditableVisit() || error())
        "
        kind="actions"
        label="Workflow actions"
      >
        <div barStart class="action-selection">
          <button
            *ngIf="isDetailAuthoring; else actionsLabel"
            type="button"
            class="detail-edit-button behavior-select"
            [class.detail-edit-button--selected]="actionSelected"
            [attr.aria-pressed]="actionSelected"
            [attr.aria-label]="
              'Configure action ' + (action()?.label || 'staff actions')
            "
            (click)="configureAction()"
            title="Configure staff action"
          >
            <i class="pi pi-pencil" aria-hidden="true"></i> Actions
          </button>
          <ng-template #actionsLabel
            ><span class="bar-label"
              ><i class="pi pi-bolt" aria-hidden="true"></i> Actions</span
            ></ng-template
          >
          <ui-form
            *ngIf="actions().length > 1"
            class="task-picker"
            subscriptSizing="dynamic"
            aria-label="Choose task"
            [config]="taskPickerConfig()"
            [formValues]="taskPickerValue()"
            (formValuesChange)="pickTask($event)"
            [disabled]="isSaving()"
            [useAutoSave]="false"
          />
          <span *ngIf="actions().length === 1" class="single-task">{{
            action()?.label
          }}</span>
          <span *ngIf="!actions().length" class="empty-actions">{{
            isWaitingForStart()
              ? data.stepProgress?.canStart
                ? "Start work to make this visit's tasks available."
                : "Tasks become available after work begins."
              : completeAction()
                ? "Review requirements, then complete this step."
                : data.isCurrent
                  ? "No task is available to you at this visit."
                  : "Read-only visit"
          }}</span>
        </div>
        <div
          barEnd
          class="primary-action"
          *ngIf="(form() || completeAction()) && !isDetailAuthoring"
        >
          <p
            *ngIf="form() && !error()"
            class="submission-hint"
            role="status"
            aria-live="polite"
          >
            {{ submissionHint() }}
          </p>
          <ui-button
            *ngIf="
              form() && !selectedFormSaved() && !selectedDecisionRecorded()
            "
            [buttonText]="saveLabel()"
            severity="primary"
            (click)="submit()"
            [disabled]="!canSubmit()"
          />
          <ui-button
            *ngIf="completeAction()"
            buttonText="Complete step"
            severity="primary"
            (click)="completeStep()"
            [disabled]="!canCompleteStep()"
          />
        </div>
        <div barFeedback class="submission-feedback">
          <p
            *ngFor="let reason of data.execution.availableActions?.blockReasons"
            class="block-notice"
          >
            {{ reason.message }}
          </p>
          <div *ngIf="error() || reloadRequired()" class="recovery-actions">
            <p *ngIf="error()" role="alert">{{ error() }}</p>
            <ui-button
              *ngIf="reloadRequired() && !previewMode"
              buttonText="Refresh current state"
              severity="secondary"
              size="small"
              (click)="load()"
              [disabled]="isSaving() || loading() || visitControlInputPending()"
              [tooltip]="visitControlInputPending() ? 'Save or cancel your changes before refreshing this workspace.' : undefined"
            />
          </div>
        </div>
      </ez-workspace-toolbar>

    <p *ngIf="previewMode" class="meta">Page preview</p>
    <section
      *ngIf="isEditableVisit() && (actions().length || completeAction() || (!previewMode && hasCurrentRequirementContext() && (completionChecklist()?.requirements?.length || checklistLoading() || checklistError())))"
      role="region" aria-label="Step requirements"
      class="requirements"
    >
      <h2 class="rail-section-title">Requirements</h2>
      <ng-container *ngIf="completionChecklist() as checklist">
      <phms-workflow-completion-checklist
        *ngIf="completeAction() || checklist.requirements.length"
        [checklist]="checklist"
        [compact]="true"
        [readOnly]="!completeAction()"
        [preview]="previewMode"
        [files]="documentFiles"
        [uploadOutcomes]="documentUploadOutcomes"
        [uploadingId]="uploadingDocument()"
        [disabled]="
          previewMode ||
          isWaitingForStart() ||
          isSaving() ||
          visitControlInputPending() ||
          loading() ||
          reloadRequired()
        "
        (requirementSelected)="revealNoticeWork()"
        (fileChange)="chooseDocument($event)"
        (uploadRequested)="uploadDocument($event)"
      />

      </ng-container>
      <p *ngIf="visitControlInputPending()" role="status">Save or cancel your changes before continuing.</p>
      <p *ngIf="checklistLoading()" role="status">
        Loading completion requirements…
      </p>
      <p *ngFor="let message of stepRequirementMessages()" class="block-notice">{{ message }}</p>
      <p *ngIf="completeAction() && hasUnsavedInput() && !visitControlInputPending()" class="block-notice">
        Save your answers and upload selected files before completing this step.
      </p>
      <div *ngIf="checklistError()" class="block-notice" role="alert">
        {{ checklistError() }}
        <ui-button
          buttonText="Retry checklist"
          severity="secondary"
          size="small"
          [disabled]="isSaving() || loading()"
          (click)="loadCompletionChecklist()"
        />
      </div>
      <button
        *ngIf="isDetailAuthoring"
        type="button"
        class="detail-edit-button behavior-select"
        [class.detail-edit-button--selected]="actionSelected"
        [attr.aria-pressed]="actionSelected"
        [attr.aria-label]="
          'Configure checks for ' + (action()?.label || 'staff actions')
        "
        (click)="configureAction()"
        title="Configure action requirements"
      >
        <i class="pi pi-pencil" aria-hidden="true"></i> Action checks
      </button>
      <details
        *ngIf="
          !isDetailAuthoring &&
          requirements().length > 0 &&
          !selectedFormSaved() &&
          !selectedDecisionRecorded()
        "
        [open]="requirementsNeedAttention()"
      >
        <summary>
          <strong>{{
            completeAction() ? "Action checks" : "Requirements"
          }}</strong
          ><span>{{ requirementsSummary() }}</span>
        </summary>
        <div class="panel-body">
          <p *ngIf="!requirements().length">
            {{
              !data.isCurrent
                ? "No active submission requirements for this recorded visit."
                : action()?.outcomes?.length
                  ? "Choose an outcome to see its current requirements."
                  : "The server checks requirements when you submit."
            }}
          </p>
          <ul>
            <li *ngFor="let check of requirements()">
              <strong>{{ check.label || check.code }}</strong> ·
              {{ requirementLabel(check) }}
            </li>
          </ul>
        </div>
      </details>
    </section>
        <p *ngIf="!isEditableVisit()">This is a recorded visit. Its saved work is read only.</p>
        <p *ngIf="isEditableVisit() && !completionChecklist()?.requirements?.length && !checklistLoading() && !checklistError() && !requirements().length">No required work for this step.</p>
      </ui-panel>
    </div>
    <div frameContext class="context" role="region" aria-label="Reference rail"
      [class.detail-context--selected]="isDetailAuthoring && detailSelection?.kind === 'context'">
      <div *ngIf="isDetailAuthoring" class="detail-region-heading" role="group" aria-label="Reference rail controls">
        <strong>Reference rail</strong>
        <button type="button" class="detail-edit-button" aria-label="Reference rail placement" (click)="configureDetail({kind:'context'})"
          [attr.aria-pressed]="detailSelection?.kind === 'context'">
          <i class="pi pi-pencil" aria-hidden="true"></i> Placement
        </button>
        <button *ngIf="allowedBlockRegions().includes('supporting')" type="button" class="detail-insert-button"
          [disabled]="detailEditingDisabled" (click)="addPageContent('supporting')">
          <i class="pi pi-plus" aria-hidden="true"></i> Add reference content
        </button>
      </div>
      <ez-workspace-block-host
        *ngIf="session && (supportingBlocks.length || isDetailAuthoring)"
        [registry]="registry"
        [session]="session"
        host="step-shell"
        [blocks]="supportingBlocks"
        [authoring]="isDetailAuthoring"
        authoringRegion="supporting"
        [authoringGroup]="historyId"
        [allowedRegions]="allowedBlockRegions()"
        [selectedInstanceId]="selectedBlock"
        [authoringEmptyTemplate]="emptyPageContent"
        (blockConfigure)="configureDetail({ kind: 'block', id: $event })"
        (blockMove)="moveBlock($event)"
      />
    </div>
  </ez-workspace-frame>
  <ng-container *ngIf="!state()" [ngTemplateOutlet]="operationalPanels" />
  <ng-template #operationalPanels>
  <phms-workflow-automated-activities
    *ngIf="!previewMode && automatedWorkflowId > 0 && (state()?.nodeTypeCode === 'automated_step' || (!state() && !loading()))"
    [workflowId]="automatedWorkflowId"
    [occurrenceId]="automatedOccurrenceId"
    [autoLoad]="state()?.nodeTypeCode === 'automated_step'"
    [interactionBlocked]="
      saving() || workflowStatusSaving() || stepStatusSaving() || startingWork() || panelDrafts.dirty() || panelDrafts.busy()
    "
    (busyChange)="automatedActivitySaving.set($event)"
    (pendingInputChange)="automatedActivityInputPending.set($event)"
    (changed)="load()"
  />
  <phms-workflow-timers-panel
    *ngIf="!previewMode && state()?.hasTimers && automatedWorkflowId > 0 && automatedOccurrenceId"
    [workflowId]="automatedWorkflowId"
    [occurrenceId]="automatedOccurrenceId"
    [definitionVersionId]="state()?.definitionVersionId ?? null"
    [readVersion]="state()?.timerReadVersion === undefined ? 1 : state()?.timerReadVersion"
    [viewedETag]="state()?.execution?.stepETag ?? null"
    [autoLoad]="timerInspectionKey() === automatedWorkflowId + '/' + automatedOccurrenceId"
    [interactionBlocked]="(isSaving() && !timerControlSaving()) || childControlInputPending() || panelDrafts.dirty()"
    (inspectionRequested)="timerInspectionKey.set($event.workflowId + '/' + $event.occurrenceId)"
    (busyChange)="timerControlSaving.set($event)"
    (pendingInputChange)="timerControlInputPending.set($event)"
    (changed)="load()"
  />
  <phms-workflow-child-work-panel
    *ngIf="!previewMode && state()?.hasChildLinks && automatedWorkflowId > 0 && automatedOccurrenceId"
    [workflowId]="automatedWorkflowId"
    [occurrenceId]="automatedOccurrenceId"
    [viewedETag]="state()?.execution?.stepETag ?? null"
    [autoLoad]="childInspectionKey() === automatedWorkflowId + '/' + automatedOccurrenceId"
    [interactionBlocked]="(isSaving() && !childControlSaving()) || timerControlInputPending() || panelDrafts.dirty()"
    (inspectionRequested)="childInspectionKey.set($event.workflowId + '/' + $event.occurrenceId)"
    (busyChange)="childControlSaving.set($event)"
    (pendingInputChange)="childControlInputPending.set($event)"
    (changed)="load()"
  />
  </ng-template>
</section>
